import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Download, FileSpreadsheet, FileText, Image as ImageIcon } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from 'components/ui/table';
import { DocumentItem, documentExt, fetchDocumentBlob, saveBlob } from './api';

const TYPE_LABEL: Record<string, string> = {
  main: 'Main contract',
  rate_sheet: 'Rate sheet',
  other: 'Other document',
};
const MAX_SHEET_ROWS = 1000;  // keep huge rate sheets responsive; download for the full file

type Sheet = { name: string; rows: string[][] };
type Preview =
  | { kind: 'pdf' | 'image'; url: string }
  | { kind: 'sheet'; sheets: Sheet[] }
  | { kind: 'none' };

const kindOf = (fileName: string): 'pdf' | 'image' | 'sheet' | 'none' => {
  const ext = documentExt(fileName);
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg'].includes(ext)) return 'image';
  if (['xlsx', 'xls'].includes(ext)) return 'sheet';
  return 'none';
};

const DocIcon: React.FC<{ fileName: string }> = ({ fileName }) => {
  const kind = kindOf(fileName);
  const cls = 'h-4 w-4 shrink-0 text-muted-foreground';
  if (kind === 'sheet') return <FileSpreadsheet className={cls} />;
  if (kind === 'image') return <ImageIcon className={cls} />;
  return <FileText className={cls} />;
};

const isBlank = (c: unknown) => String(c ?? '').trim() === '';

// Excel's used range often runs far past the data (cells once formatted, then cleared),
// so drop trailing blank rows and columns. Blank rows inside the data are kept.
function trimSheet(rows: string[][]): string[][] {
  let end = rows.length;
  while (end > 0 && rows[end - 1].every(isBlank)) end--;
  const kept = rows.slice(0, end);
  const width = Math.max(0, ...kept.map((r) => {
    let w = r.length;
    while (w > 0 && isBlank(r[w - 1])) w--;
    return w;
  }));
  return kept.map((r) => Array.from({ length: width }, (_, i) => r[i] ?? ''));
}

async function toSheets(blob: Blob): Promise<Sheet[]> {
  const wb = XLSX.read(await blob.arrayBuffer(), { type: 'array' });
  return wb.SheetNames.map((name) => ({
    name,
    rows: trimSheet(XLSX.utils.sheet_to_json<string[]>(wb.Sheets[name], { header: 1, defval: '', raw: false })),
  }));
}

// ---- Documents tab: preview + download the version's stored files (read-only) ----
// `openAtPage`: open the main contract at this page (a field's "Source" link points here);
// `openDocumentId`: open this document instead (e.g. a rate sheet that couldn't be read).
// `revisionOf`: rate set id -> revision number, to label rate sheets of a rate revision.
const ContractDocumentsTab: React.FC<{
  contractId: number; documents: DocumentItem[]; openAtPage?: number | null;
  openDocumentId?: number | null; revisionOf?: Record<number, number>;
}> = ({ contractId, documents, openAtPage, openDocumentId, revisionOf = {} }) => {
  const mainId = documents.find((d) => d.document_type === 'main')?.id;
  const [selectedId, setSelectedId] = useState<number | null>(
    (documents.find((d) => d.id === openDocumentId) ?? documents.find((d) => d.document_type === 'main') ?? documents[0])?.id ?? null,
  );
  useEffect(() => { if (openDocumentId) setSelectedId(openDocumentId); }, [openDocumentId]);
  const page = openAtPage && selectedId === mainId ? openAtPage : null;
  const [preview, setPreview] = useState<Preview | null>(null);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const blobs = useRef(new Map<number, Blob>());  // fetched once, reused for preview + download

  const getBlob = async (doc: DocumentItem) => {
    let blob = blobs.current.get(doc.id);
    if (!blob) {
      blob = await fetchDocumentBlob(contractId, doc);
      blobs.current.set(doc.id, blob);
    }
    return blob;
  };

  const selected = documents.find((d) => d.id === selectedId) ?? null;

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    let url: string | null = null;
    setPreview(null);
    setError(null);
    setSheetIdx(0);
    (async () => {
      try {
        const blob = await getBlob(selected);
        const kind = kindOf(selected.file_name);
        let next: Preview;
        if (kind === 'pdf' || kind === 'image') {
          url = URL.createObjectURL(blob);
          next = { kind, url };
        } else if (kind === 'sheet') {
          next = { kind, sheets: await toSheets(blob) };
        } else {
          next = { kind: 'none' };
        }
        if (!cancelled) setPreview(next);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load the document');
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, contractId]);

  const download = async (doc: DocumentItem) => {
    setError(null);
    try {
      saveBlob(await getBlob(doc), doc.file_name);
    } catch (e: any) {
      setError(e?.message || 'Download failed');
    }
  };

  if (documents.length === 0) {
    return <p className="text-sm text-muted-foreground">No documents were uploaded for this contract.</p>;
  }

  const sheet = preview?.kind === 'sheet' ? preview.sheets[sheetIdx] : null;

  return (
    <div className="grid grid-cols-[260px_1fr] gap-4">
      {/* document list */}
      <div className="space-y-2">
        {documents.map((d) => (
          <div
            key={d.id}
            onClick={() => setSelectedId(d.id)}
            className={`rounded-md border px-3 py-2 cursor-pointer flex items-center gap-2 ${d.id === selectedId ? 'border-blue-600 bg-blue-50' : 'hover:bg-muted'}`}
          >
            <DocIcon fileName={d.file_name} />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-muted-foreground">
                {TYPE_LABEL[d.document_type] || d.document_type}{d.shared ? ' (shared terms)' : ''}
                {d.rate_set_id && revisionOf[d.rate_set_id] ? ` · Revision R${revisionOf[d.rate_set_id]}` : ''}
                {d.parse_status === 'failed' || d.parse_status === 'no_rows' ? <span className="text-red-600"> · not read</span> : null}
              </div>
              <div className="text-sm truncate" title={d.file_name}>{d.file_name}</div>
            </div>
            <Button
              size="sm" variant="ghost" title="Download"
              onClick={(e) => { e.stopPropagation(); download(d); }}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* viewer */}
      <div className="rounded-md border min-h-[80vh] flex flex-col">
        {error && <div className="m-3 rounded bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
        {!error && !preview && <div className="m-auto text-sm text-muted-foreground">Loading…</div>}

        {preview?.kind === 'pdf' && (
          <iframe key={`${preview.url}#${page ?? ''}`} title={selected?.file_name}
            src={page ? `${preview.url}#page=${page}` : preview.url} className="w-full flex-1 min-h-[80vh] rounded-md" />
        )}
        {preview?.kind === 'image' && (
          <div className="flex-1 overflow-auto p-3">
            <img src={preview.url} alt={selected?.file_name} className="max-w-full mx-auto" />
          </div>
        )}
        {preview?.kind === 'none' && selected && (
          <div className="m-auto text-center space-y-2">
            <p className="text-sm text-muted-foreground">No preview for this file type.</p>
            <Button variant="outline" onClick={() => download(selected)}>Download {selected.file_name}</Button>
          </div>
        )}
        {preview?.kind === 'sheet' && (
          <div className="flex-1 overflow-auto p-3 space-y-3">
            {preview.sheets.length > 1 && (
              <div className="flex gap-1 flex-wrap">
                {preview.sheets.map((s, i) => (
                  <Button key={s.name} size="sm" variant={i === sheetIdx ? 'default' : 'outline'} onClick={() => setSheetIdx(i)}>
                    {s.name}
                  </Button>
                ))}
              </div>
            )}
            {sheet && sheet.rows.length === 0 && <p className="text-sm text-muted-foreground">This sheet is empty.</p>}
            {sheet && sheet.rows.length > 0 && (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>{sheet.rows[0].map((c, i) => <TableHead key={i}>{c}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {sheet.rows.slice(1, MAX_SHEET_ROWS + 1).map((r, i) => (
                      <TableRow key={i}>{r.map((c, j) => <TableCell key={j}>{c}</TableCell>)}</TableRow>
                    ))}
                  </TableBody>
                </Table>
                {sheet.rows.length - 1 > MAX_SHEET_ROWS && (
                  <p className="text-xs text-muted-foreground">
                    Showing the first {MAX_SHEET_ROWS} of {sheet.rows.length - 1} rows — download for the full sheet.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractDocumentsTab;
